import React, { useState, useEffect, useContext } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../api/axiosConfig";
import "../../styles/Settings/Settings.css";
import socket from "../../socket";
import { ToastContainer, toast } from "react-toastify";
import { ProjectContext } from "../../App";
import { useRolePermissions } from "./RoleContext";
const fetchData = async (url, setData, setError, setLoading) => {
  setLoading(true);
  try {
    const response = await api.get(url);
    setData(response.data);
    setLoading(false);
  } catch (error) {
    console.error("Error fetching data:", error);
    setError(error);
  } finally {
    setLoading(false);
  }
};

const RoleSettings = () => {
  const { hasPermission } = useRolePermissions();
  const { isNavBarOpen } = useContext(ProjectContext);
  const [roleSettingOpen, setRoleSettingOpen] = useState(false);
  const [rolePermissions, setRolePermissions] = useState({});
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [roleDialboxOpen, setRoleDialboxOpen] = useState(false);
  const [moduleDialboxOpen, setModuleDialboxOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: "" });
  const [newModule, setNewModule] = useState({ name: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removePermissions, setRemovePermissions] = useState(new Set());
  const [userRole, setUserRole] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState();

  const createPermissionsMap = (permissionsData) => {
    const rolePermissions = {};
    permissionsData.forEach((item) => {
      const { role_name, module_name, action_name } = item;
      if (!rolePermissions[role_name]) rolePermissions[role_name] = {};
      if (!rolePermissions[role_name][module_name])
        rolePermissions[role_name][module_name] = [];
      rolePermissions[role_name][module_name].push(action_name);
    });
    return rolePermissions;
  };

  useEffect(() => {
    fetchData("/api/roles", setRoles, setError, setLoading);
    fetchData("/api/modules", setModules, setError, setLoading);
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await api.get("/api/rolePermissions");
        const permissionsMap = createPermissionsMap(response.data);
        setRolePermissions(permissionsMap);
      } catch (error) {
        console.error("Failed to fetch permissions", error);
      }
    };
    fetchPermissions();
  }, []);

  useEffect(() => {
    socket.on("role added", (newRole) => {
      setRoles((prev) => [...prev, newRole]); // ✅ Correct way to update state
    });
    socket.on("module added", (newModule) => {
      setModules((prev) => [...prev, newModule]); // ✅ Correct way to update state
    });
    socket.on("role deleted", (roleId) => {
      setRoles((prevRoles) => prevRoles.filter((r) => r.id !== Number(roleId))); // ✅ Fix applied
    });

    return () => {
      socket.off("role added");
      socket.off("role deleted");
      socket.off("module added");
    };
  }, []);

  const updatePermissions = async () => {
    const permissionsToUpdate = rolePermissions[userRole];
    if (!permissionsToUpdate) {
      toast.error("No permissions to update.", {
        position: "bottom-center",
        autoClose: 2000,
      });
      return;
    }
    try {
      await api.put(`/api/rolepermissions/${userRole}`, {
        permissions: permissionsToUpdate,
        removePermissions: Array.from(removePermissions),
      });
      setRoleSettingOpen(false);
      // window.location.reload();
    } catch (error) {
      toast.error("Failed to update permissions", error, {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  const handleCheckboxChange = (role, category, action) => {
    setRolePermissions((prevPermissions) => {
      const currentPermissions = prevPermissions[role]?.[category] || [];
      const updatedPermissions = currentPermissions.includes(action)
        ? currentPermissions.filter((perm) => perm !== action)
        : [...currentPermissions, action];

      setRemovePermissions((prev) => {
        const updatedSet = new Set(prev);
        if (currentPermissions.includes(action)) {
          updatedSet.add({ module: category, action });
        } else {
          updatedSet.delete({ module: category, action });
        }
        return updatedSet;
      });

      return {
        ...prevPermissions,
        [role]: {
          ...prevPermissions[role],
          [category]: updatedPermissions,
        },
      };
    });
  };

  const handleEditRole = (roleName) => {
    setUserRole(roleName);
    setRoleSettingOpen(true);
    setRemovePermissions(new Set());
  };

  const handleAddNewRole = async () => {
    try {
      // Check if the role name is not empty
      if (!newRole.name.trim()) {
        toast.error("Role name cannot be empty", {
          position: "bottom-center",
          autoClose: 2000,
        });
        return;
      }

      // Send POST request to create a new role
      const response = await api.post("/api/roles", { name: newRole.name });
      // Handle the response
      if (response.status === 201) {
        setRoles((prevRoles) => [...prevRoles, response.data]);
        setRoleDialboxOpen(false);
        setNewRole({ name: "" }); // Clear the input field
      }
    } catch (error) {
      console.error("Failed to add new role:", error);
      toast.error("Failed to add role. Please try again.", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };
  const handleAddNewModule = async () => {
    try {
      // Check if the role name is not empty
      if (!newModule.name.trim()) {
        toast.error("Module name cannot be empty", {
          position: "bottom-center",
          autoClose: 2000,
        });
        return;
      }

      // Send POST request to create a new role
      const response = await api.post("/api/modules", { name: newModule.name });
      // Handle the response
      if (response.status === 201) {
        setModules((prevModules) => [...prevModules, response.data]);
        setModuleDialboxOpen(false);
        setNewModule({ name: "" }); // Clear the input field
        // window.location.reload();
      }
    } catch (error) {
      console.error("Failed to add new module:", error);
      toast.error("Failed to add module. Please try again.", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      // Send DELETE request to the backend
      const response = await api.delete(`/api/roles/${roleId}`);
      if (response.data.message === "Role deleted successfully") {
        // Optimistically remove the deleted role from the list
        setRoles((prevRoles) => prevRoles.filter((role) => role.id !== roleId));
        setDeleteDialogOpen(false);
        setIsCheckboxChecked(false);
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Failed to delete the role.", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  return (
    <div>
      <ToastContainer />
      {hasPermission("roles", "create") && (
        <button
          className="add-btn role-btn"
          onClick={() => setRoleDialboxOpen(true)}
        >
          Add Role
        </button>
      )}
      {hasPermission("roles", "create") && (
        <button
          className="add-module-btn"
          onClick={() => setModuleDialboxOpen(true)}
        >
          Add Module
        </button>
      )}
      {loading ? (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <span className="loading-animation">LOADING...</span>
        </div>
      ) : error ? (
        <p>Error loading roles: {error.message}</p>
      ) : (
        <ul className="role-settings-ul">
          {roles?.length ? (
            roles.map((role) => (
              <li key={role.id}>
                <div className="role-list">
                  <span
                    className="initial"
                    style={{
                      marginRight: "16px",
                      marginTop: "8px",
                      height: "30px",
                      width: "30px",
                      fontSize: "16px",
                    }}
                  >
                    {role.name ? role.name.slice(0, 2).toUpperCase() : ""}
                  </span>
                  <div className="role-right-container">
                    <div>
                      <h3 className="role-name">
                        {role.name ? role.name.replace("_", " ") : role.name}
                        <span style={{ marginLeft: "15px", fontSize: "12px" }}>
                          {role.user_count ? role.user_count : ""}
                        </span>
                      </h3>
                      <p className="role-description">
                        User Profile Last Modified By Admin{" "}
                      </p>
                    </div>
                    <div
                      className={
                        isNavBarOpen
                          ? "actions-container"
                          : "actions-container-close"
                      }
                    >
                      {hasPermission("roles", "update") && (
                        <span>
                          <EditIcon
                            style={{ color: "blue", cursor: "pointer" }}
                            onClick={() => handleEditRole(role.name)}
                          />
                        </span>
                      )}
                      {hasPermission("roles", "delete") && (
                        <span>
                          <DeleteIcon
                            style={{ color: "red", cursor: "pointer" }}
                            onClick={() => {
                              setDeleteDialogOpen(true);
                              setRoleToDelete(role.id);
                            }}
                          />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <p>Loading roles...</p>
          )}
        </ul>
      )}
      {roleSettingOpen && (
        <div className="edit-role-settings-con">
          <div className="role-scroll-con">
            <div className="edit-role-container">
              <h3
                style={{
                  backgroundColor: "transparent",
                  marginTop: "5px",
                  fontSize: "15px",
                }}
              >
                Edit Role Settings for{" "}
                {userRole === "Project_Manager" ? "Project Manager" : userRole}
              </h3>
            </div>
            <div className="role-permission-mapping-container">
              <div className="action-div">
                <p className="actions">Create</p>
                <p className="actions">Read</p>
                <p className="actions">Update</p>
                <p className="actions">Delete</p>
              </div>
              {modules.map((module) => (
                <div key={module.id} className="role-select-con">
                  <h3
                    style={{
                      marginRight: "0px",
                      fontSize: "15px",
                      fontWeight: "100",
                    }}
                  >
                    {module.name.charAt(0).toUpperCase() + module.name.slice(1)}
                  </h3>
                  <div className="checkbox-container">
                    {["create", "read", "update", "delete"].map((action) => (
                      <div key={action}>
                        <input
                          type="checkbox"
                          checked={rolePermissions[userRole]?.[
                            module.name
                          ]?.includes(action)}
                          className="action-label"
                          onChange={() =>
                            handleCheckboxChange(userRole, module.name, action)
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="role-btn-con">
              <button onClick={updatePermissions} className="add-btn save-role">
                Save Permissions
              </button>
              <button
                onClick={() => setRoleSettingOpen(false)}
                className="role-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {roleDialboxOpen && (
        <div className="add-role-dial">
          <div className="add-role-settings">
            <div className="add-role-header">
              <h3 className="add-new-user-heading">Add New Role</h3>
            </div>
            <h3 className="label-header-element">Role Name</h3>
            <input
              className="user-element"
              placeholder="Enter Role Name"
              type="text"
              value={newRole.name}
              onChange={(e) => setNewRole({ name: e.target.value })}
            />
            <div className="role-btn-container">
              <button className="add-btn" onClick={handleAddNewRole}>
                Add
              </button>
              <button
                className="cancel-btn"
                onClick={() => {
                  setRoleDialboxOpen(false);
                  setNewRole([]);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {moduleDialboxOpen && (
        <div className="add-role-dial">
          <div className="add-role-settings">
            <div className="add-role-header">
              <h3 className="add-new-user-heading">Add New Module</h3>
            </div>
            <h3 className="label-header-element">Module Name</h3>
            <input
              className="user-element"
              placeholder="Enter Module Name"
              type="text"
              value={newModule.name}
              onChange={(e) => setNewModule({ name: e.target.value })}
            />
            <div className="role-btn-container">
              <button className="add-btn" onClick={handleAddNewModule}>
                Add
              </button>
              <button
                className="cancel-btn"
                onClick={() => {
                  setModuleDialboxOpen(false);
                  setNewModule([]);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteDialogOpen && (
        <div className="delete-phase-dial">
          <div className="delete-dial">
            <div className="delete-header">
              <h3
                style={{
                  backgroundColor: "transparent",
                  marginLeft: "15px",
                  fontSize: "15px",
                }}
              >
                Delete Role
              </h3>
            </div>
            <div style={{ backgroundColor: "white" }}>
              <p style={{ backgroundColor: "white" }}>
                Are you sure you want to delete this Role. This action cannot be
                undone.
              </p>
              <input
                style={{ cursor: "pointer" }}
                type="checkbox"
                id="delete-phase"
                checked={isCheckboxChecked}
                onChange={() => setIsCheckboxChecked((prev) => !prev)}
              />
              <label
                htmlFor="delete-phase"
                style={{ backgroundColor: "white", cursor: "pointer" }}
              >
                I understand this action cannot be undone.
              </label>
            </div>
            <div className="delete-dial-btn-con">
              <button
                onClick={() => handleDeleteRole(roleToDelete)}
                color="secondary"
                variant="contained"
                disabled={!isCheckboxChecked}
                className="add-btn"
                style={{ backgroundColor: isCheckboxChecked ? "red" : "gray" }}
              >
                Delete
              </button>

              <button
                onClick={() => {
                  setDeleteDialogOpen(false);
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleSettings;
